import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { isAdminUid } from '../admin/adminAllowlist'

// Inline account button + dropdown. Parent decides the positioning —
// previously this component wrapped itself in `fixed top-3 right-3
// z-[9998]` which worked for pages without their own header but
// covered the LibraryView settings button on mobile (where the header
// bar was right where the fixed pill landed). Now it renders as a
// plain relative-positioned button, and callers slot it wherever it
// fits in their own layout.
//
// The dropdown is still absolute-positioned against the trigger, so
// it floats over neighbouring content when opened regardless of where
// the parent placed the button.
export function AccountMenu() {
  const { user, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!user) return null

  const label = user.displayName || user.email || 'Account'
  const initial = (label[0] || 'G').toUpperCase()
  const isAdmin = isAdminUid(user.uid)

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-full bg-bg-secondary p-1 text-sm text-text-primary transition-colors hover:bg-bg-tertiary"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
      >
        <span className="grid h-7 w-7 place-items-center rounded-full bg-accent-primary text-xs font-semibold text-white">
          {initial}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 overflow-hidden rounded-lg border border-border-primary bg-bg-secondary shadow-lg z-50"
        >
          <div className="border-b border-border-primary px-3 py-2 text-[11px] uppercase tracking-wider text-text-tertiary">
            Signed in as
          </div>
          <div className="truncate border-b border-border-primary px-3 py-2 text-xs text-text-secondary">
            {user.email}
          </div>

          <Link
            to="/download"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-sm text-text-primary hover:bg-bg-tertiary"
          >
            Download desktop app
          </Link>

          {isAdmin && (
            <Link
              to="/admin/keys"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-sm text-text-primary hover:bg-bg-tertiary"
            >
              Manage invite keys
            </Link>
          )}

          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              setOpen(false)
              await signOut()
            }}
            className="block w-full border-t border-border-primary px-3 py-2 text-left text-sm text-red-400 hover:bg-bg-tertiary"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}
